/**
 * Layout preferences — currently just the per-user top-navbar visibility.
 * Default is false (hidden) so the sidebar is the only chrome unless the
 * user explicitly enables the bar from MyProfile → Preferences.
 *
 * Persistence: localStorage for instant boot + PATCH to
 *   /api/v1/user-preferences/context  {show_top_navbar: bool}
 * which lands in maple.user_preferences.user_context.show_top_navbar.
 */

import { create } from 'zustand';
import { UserPreferencesService } from '@/lib/api/user-preferences-service';

export const NAVBAR_STORAGE_KEY = 'nue-show-top-navbar';

const readInitialFromLS = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem(NAVBAR_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

const applyToDom = (show: boolean) => {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-show-top-navbar', show ? 'true' : 'false');
};

interface LayoutPrefsState {
  showTopNavbar: boolean;
  hydrated: boolean;
  setShowTopNavbar: (v: boolean) => void;
  hydrateFromBackend: () => Promise<void>;
}

export const useLayoutPrefsStore = create<LayoutPrefsState>((set, get) => ({
  showTopNavbar: readInitialFromLS(),
  hydrated: false,

  setShowTopNavbar: (v) => {
    set({ showTopNavbar: v });
    applyToDom(v);
    if (typeof window !== 'undefined') {
      try { localStorage.setItem(NAVBAR_STORAGE_KEY, String(v)); } catch { /* ignore */ }
    }
    UserPreferencesService.updateUserContext({ show_top_navbar: v }).catch(() => {});
  },

  hydrateFromBackend: async () => {
    if (typeof window === 'undefined') return;
    // Make sure DOM reflects current state (in case boot script didn't run, e.g. dev HMR).
    applyToDom(get().showTopNavbar);
    try {
      const ctx = await UserPreferencesService.getUserContext();
      const v = ctx?.show_top_navbar;
      if (typeof v === 'boolean' && v !== get().showTopNavbar) {
        set({ showTopNavbar: v });
        applyToDom(v);
        try { localStorage.setItem(NAVBAR_STORAGE_KEY, String(v)); } catch { /* ignore */ }
      }
    } catch {
      // Not authenticated yet, or backend unavailable — localStorage value sticks.
    } finally {
      set({ hydrated: true });
    }
  },
}));
